import { useMemo } from 'react';
import { NotificationLevel, type NotificationTypeFieldsFragment } from '@/graphql/types';
import { extractNodes } from 'assets/js/utils/relay';
import { faBell, faFilterCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { BasePage } from '../BasePage';
import Pagination from '../../table/Pagination';
import Table from '../../table/Table';
import type { TableColumn } from '../../table/Table';
import NotificationDetail from './components/NotificationDetail';
import DateRangePicker from '../../dateRangePicker/DateRangePicker';
import NotificationCheckbox from './components/NotificationCheckbox';
import { format } from '../../../utils/formatting';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';

import { useOrderAndPaginationControl } from '../../table/hooks';
import { useNotificationQueryGenerator, useFiltrationControl } from './utils';
import MeasuringPointSelect from '../../measuringPointSelect/MeasuringPointSelect';
import SearchField from './components/SearchField';
import { EmptyState } from '../../emptyState/EmptyState';

const EMPTY_STATE_VARIANT = 'brand';
const NOTIFICATIONS_EMPTY_STATE_ICON = faBell;
const CLEAR_FILTERS_ICON = faFilterCircleXmark;
export const NOTIFICATIONS_TABLE_SORT = {
    tableId: 'notifications',
    sortableFields: ['createdAt'],
} as const;

function NotificationsPage() {
    const filtrationControl = useFiltrationControl();
    const {
        filters,
        setLevelsFilter,
        setDateRangeFilter,
        setMeasuringPointFilter,
        setSearchFilter,
    } = filtrationControl;
    const orderAndPaginationControl = useOrderAndPaginationControl(NOTIFICATIONS_TABLE_SORT);
    const { notifications, loading, updating } = useNotificationQueryGenerator(
        orderAndPaginationControl,
        filtrationControl
    );

    const columns = useMemo<TableColumn<NotificationTypeFieldsFragment>[]>(
        () => [
            {
                key: 'title',
                title: gettext('NOTIFICATIONS'),
                render(_column, item) {
                    return <NotificationDetail key={item.id} notification={item} />;
                },
            },
            {
                key: 'createdAt',
                title: gettext('Date/Time'),
                render(_column, { createdAt }) {
                    return (
                        <p className="pr-4" data-datetime={createdAt} data-datetime-target="text">
                            {format(createdAt, { useBrowserTimezone: true })}
                        </p>
                    );
                },
            },
        ],
        []
    );

    const title = gettext('NOTIFICATIONS');
    const nodes = useMemo(() => extractNodes(notifications), [notifications]);
    function resetNotficationFilters(): void {
        setDateRangeFilter('', '');
        setMeasuringPointFilter('');
        setSearchFilter('');
        Object.values(NotificationLevel).forEach((level) => setLevelsFilter(level, true));
    }

    return (
        <BasePage title={title}>
            <div className="mb-14 pb-4 pt-2">
                <div className="flex w-full flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-col gap-1 md:w-1/2 md:flex-row md:flex-wrap">
                        {Object.values(NotificationLevel).map((level, idx) => (
                            <NotificationCheckbox
                                key={idx}
                                level={level}
                                isChecked={filters.levels[level] ?? false}
                                onChange={() => setLevelsFilter(level, !filters.levels[level])}
                            />
                        ))}
                    </div>
                    <div className="w-full md:ml-auto md:flex md:w-auto md:shrink-0 md:justify-end">
                        <DateRangePicker
                            startDate={filters.startDate}
                            endDate={filters.endDate}
                            onDateRangeChange={setDateRangeFilter}
                        />
                    </div>
                </div>
                <div className="mb-4 flex w-full flex-col gap-4 md:flex-row md:items-center">
                    <div className="w-full md:w-1/2">
                        <MeasuringPointSelect
                            className="w-full"
                            selectId="mp-selector-notifications-page"
                            onSelectChange={setMeasuringPointFilter}
                            initialValue={filters.mpPk}
                            allowNoSelection={true}
                        />
                    </div>
                    <span className="flex w-full items-center justify-end space-x-2 md:w-auto"></span>
                </div>

                <div className="mb-14">
                    <div className="mb-4 flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="w-full md:w-1/2">
                            <SearchField value={filters.searchInput} onChange={setSearchFilter} />
                        </div>
                        <span className="flex w-full flex-wrap items-center justify-center gap-2 md:w-auto md:flex-nowrap md:justify-end">
                            {updating && (
                                <LoadingSpinner className="h-5 align-middle text-gray-400" />
                            )}
                            {orderAndPaginationControl.showPagination && (
                                <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                            )}
                        </span>
                    </div>
                    {loading || updating || nodes.length > 0 ? (
                        <Table
                            data={nodes}
                            columns={columns}
                            header={title}
                            loading={loading || updating}
                            orderAndPaginationControl={orderAndPaginationControl}
                            showBorder={false}
                            showDivider={false}
                            wrapperClasses="bg-white w-full mx-auto"
                            loaderClasses=""
                            headerRowClasses="border-2"
                            headerCellClasses="p-2 text-xs md:p-4 md:text-sm"
                            rowClasses="py-4 border-2 hover:bg-main-background hover:cursor-pointer"
                            rowCellClasses="text-xs sm:text-sm"
                        />
                    ) : (
                        <EmptyState
                            variant={EMPTY_STATE_VARIANT}
                            iconFontAwesome={NOTIFICATIONS_EMPTY_STATE_ICON}
                            header={gettext('NOTIFICATIONS_EMPTY_HEADING')}
                            description={gettext('NOTIFICATIONS_EMPTY_BODY')}
                            actionButtonText={gettext('CLEAR_FILTERS')}
                            actionButtonIconFontAwesome={CLEAR_FILTERS_ICON}
                            actionButtonMethod={resetNotficationFilters}
                        />
                    )}
                </div>
            </div>
        </BasePage>
    );
}

export default NotificationsPage;
